import type { ContentItem } from "@/lib/api";
import {
	applyEventTabDefaults,
	type PartialEventFilters,
} from "@/lib/event-filters";

const SCROLL_POSITION_KEYS = new Set([
	"page",
	"beforeOccurredAt",
	"beforeId",
	"afterOccurredAt",
	"afterId",
]);

/** Returns a stable identity for filters excluding scroll-position params. */
export function getFilterIdentity(
	filters: PartialEventFilters,
): Omit<
	PartialEventFilters,
	"page" | "beforeOccurredAt" | "beforeId" | "afterOccurredAt" | "afterId"
> {
	const {
		page,
		beforeOccurredAt,
		beforeId,
		afterOccurredAt,
		afterId,
		...rest
	} = filters;
	return rest;
}

export function mergeEventTabFilters(
	current: PartialEventFilters,
	updates: Partial<PartialEventFilters>,
): PartialEventFilters {
	const merged = {
		...applyEventTabDefaults(current),
		...updates,
	};
	const changedKeys = Object.keys(updates);
	const scrollOnly =
		changedKeys.length > 0 &&
		changedKeys.every((key) => SCROLL_POSITION_KEYS.has(key));

	if (!scrollOnly) {
		merged.page = undefined;
		merged.beforeOccurredAt = undefined;
		merged.beforeId = undefined;
		merged.afterOccurredAt = undefined;
		merged.afterId = undefined;
		return merged;
	}

	if ("beforeOccurredAt" in updates || "beforeId" in updates) {
		merged.afterOccurredAt = undefined;
		merged.afterId = undefined;
	}

	if ("afterOccurredAt" in updates || "afterId" in updates) {
		merged.beforeOccurredAt = undefined;
		merged.beforeId = undefined;
	}

	return merged;
}

export function getThreadGroupKey(
	item: Pick<ContentItem, "id" | "origin_id" | "root_origin_id">,
): string {
	return item.root_origin_id || item.origin_id || String(item.id);
}

/**
 * An event plus the earlier states it superseded, newest-first. When an action
 * runs (pending → executing → completed) the append-only log emits one row per
 * state, each linked to the prior via `supersedes_event_id`. Rendering all of
 * them as sibling timeline nodes buries the *current* state in near-duplicate
 * rows, so we collapse the chain to its head (the latest, un-superseded state)
 * and hang the predecessors off `history` for an on-demand "how it got here".
 */
interface CollapsedItem {
	/** The latest state in the chain — what the timeline node renders. */
	head: ContentItem;
	/** Earlier superseded states, newest-first; empty for a plain event. */
	history: ContentItem[];
	/**
	 * The run this node belongs to, when its events originated from one. Set for
	 * operation chains and their satellite events (the "needs approval"
	 * notification); null for plain content. Used to surface a "Run #N" identity
	 * and to fold same-run satellites into one node.
	 */
	runId: number | null;
}

/** True when this event is a delete_knowledge tombstone superseding a deleted event. */
function isTombstoneEvent(
	item: Pick<ContentItem, "semantic_type" | "metadata">,
): boolean {
	return (
		item.semantic_type === "tombstone" || item.metadata?.tombstone === true
	);
}

/** True when this event is an operation/approval state (has a real status). */
function isOperationState(item: ContentItem): boolean {
	return (
		item.interaction_type === "approval" ||
		item.semantic_type === "operation" ||
		item.interaction_status != null
	);
}

/**
 * Collapse supersede chains within a page of content, then fold same-run
 * satellite events into their run's node.
 *
 * Two passes:
 *  1. Supersede collapse — items linked by `supersedes_event_id` (child → the id
 *     it replaced) fold to their head (latest, un-superseded state), with
 *     predecessors hung off `history` newest-first. Only chains fully present in
 *     `contents` collapse; a predecessor that paginated onto another page stays
 *     standalone, so nothing is hidden.
 *  2. Run fold — a run emits more than its operation chain: the "needs approval"
 *     notification is a separate event sharing the run's `run_id` but *not* the
 *     supersede chain. Rendering it as its own node duplicates the run on the
 *     timeline. So we fold each run's satellite events into the run's node (the
 *     one whose head is the operation state), pushing them into `history`.
 *
 * Order and identity of surviving heads are preserved.
 */
export function collapseSupersedeChains(
	contents: ContentItem[],
	{ foldRunSatellites = true }: { foldRunSatellites?: boolean } = {},
): CollapsedItem[] {
	const byId = new Map<number, ContentItem>();
	for (const item of contents) byId.set(item.id, item);

	// An event is superseded (not a head) iff some other in-page item points at
	// it via supersedes_event_id.
	const supersededIds = new Set<number>();
	for (const item of contents) {
		if (
			item.supersedes_event_id != null &&
			byId.has(item.supersedes_event_id)
		) {
			supersededIds.add(item.supersedes_event_id);
		}
	}

	const collapsed: CollapsedItem[] = [];
	for (const item of contents) {
		if (supersededIds.has(item.id)) continue; // folded into its successor's history

		// Walk backwards from this head down the supersedes links, collecting the
		// earlier states that are present in this page.
		const history: ContentItem[] = [];
		let cursor = item.supersedes_event_id;
		const guard = new Set<number>([item.id]); // cycle guard for malformed chains
		while (cursor != null && byId.has(cursor) && !guard.has(cursor)) {
			const prev = byId.get(cursor)!;
			history.push(prev);
			guard.add(cursor);
			cursor = prev.supersedes_event_id;
		}

		// A tombstone head marks its entire supersede chain as deleted, so omit the
		// chain rather than rendering an empty tombstone row on content_ids reads.
		if (isTombstoneEvent(item)) continue;

		collapsed.push({ head: item, history, runId: item.run_id ?? null });
	}

	// Results hosts keep every same-run output as its own row.
	if (!foldRunSatellites) return collapsed;

	// Pass 2 — fold same-run satellites into the run's operation node. A run's
	// node is the collapsed item whose head is the operation state; every other
	// same-run collapsed item (the notification) folds into it. If a run has no
	// operation state on the page (only a notification survived), the satellite
	// stays standalone rather than vanishing.
	const opNodeIndexByRun = new Map<number, number>();
	collapsed.forEach((node, index) => {
		if (node.runId != null && isOperationState(node.head)) {
			// First operation node wins the run (there is normally exactly one head
			// per run after supersede collapse).
			if (!opNodeIndexByRun.has(node.runId)) {
				opNodeIndexByRun.set(node.runId, index);
			}
		}
	});

	const foldedInto = new Set<number>(); // indices absorbed as satellites
	collapsed.forEach((node, index) => {
		if (node.runId == null || isOperationState(node.head)) return;
		const targetIndex = opNodeIndexByRun.get(node.runId);
		if (targetIndex == null || targetIndex === index) return;
		// Append the satellite (e.g. the approval notification) plus any of its own
		// history into the run node's history, then drop it as a top-level node.
		const target = collapsed[targetIndex];
		target.history.push(node.head, ...node.history);
		foldedInto.add(index);
	});

	return collapsed.filter((_, index) => !foldedInto.has(index));
}

interface ThreadGroup {
	root: ContentItem | null;
	replies: ContentItem[];
	hasParentContext: boolean;
}

export function groupContentByThread(
	contents: ContentItem[],
): Map<string, ThreadGroup> {
	const groups = new Map<string, ThreadGroup>();

	for (const item of contents) {
		const rootId = getThreadGroupKey(item);
		if (!groups.has(rootId)) {
			groups.set(rootId, { root: null, replies: [], hasParentContext: false });
		}

		const group = groups.get(rootId);
		if (!group) {
			continue;
		}

		if (item.depth === 0) {
			group.root = item;
			group.hasParentContext = !!item.parent_context;
		} else {
			group.replies.push(item);
		}
	}

	return groups;
}

/**
 * One timeline node after supersede-collapse and thread-grouping: either a
 * thread root or a standalone event, with its folded history and run.
 */
export interface TimelineNode {
	rootId: string;
	group: ThreadGroup;
	history: ContentItem[];
	runId: number | null;
}

export interface ConsecutiveCallFold {
	/** First node id + size: stable while the loaded set is unchanged. */
	key: string;
	toolName: string;
	/** Null for actor-less rows (session-token calls disclose no actor). */
	clientId: string | null;
	nodes: TimelineNode[];
	/** Newest row's timestamp, for the right-aligned clock. */
	newestAt: string | Date | number | null;
	/** Oldest row's timestamp; a wide span renders as a start–end range. */
	oldestAt: string | Date | number | null;
	/** Summed across the folded rows; null when no row reported one. */
	totalDurationMs: number | null;
	totalSdkCalls: number | null;
}

export type FoldedTimelineEntry =
	| { kind: "single"; node: TimelineNode }
	| { kind: "fold"; fold: ConsecutiveCallFold };

function foldableAuditNode(node: TimelineNode): {
	toolName: string;
	clientId: string | null;
	scopeId: string | null;
} | null {
	// Only plain successful tool calls fold: something with replies, history,
	// or a run keeps its own node, and a failed call always stands alone —
	// failures are what a scan is looking for, never bury them.
	if (
		node.runId != null ||
		node.history.length > 0 ||
		node.group.replies.length > 0
	) {
		return null;
	}
	const root = node.group.root;
	if (!root) return null;
	if (
		root.semantic_type !== "audit" ||
		root.origin_type !== "tool_invocation"
	) {
		return null;
	}
	// An orphaned root carries its missing parent's context card — folding
	// it would hide that context.
	if (root.parent_context) return null;
	const payload = (root.payload_data ?? {}) as Record<string, unknown>;
	if (payload.success !== true) return null;
	const toolName =
		typeof payload.tool_name === "string" && payload.tool_name.trim()
			? payload.tool_name.trim()
			: null;
	if (!toolName) return null;
	// Session-token calls (the app's own list reads) disclose no actor on
	// list rows, and their cards show no actor chip — keying them on tool
	// alone hides nothing the UI would otherwise show.
	const clientId =
		typeof root.client_id === "string" && root.client_id.trim()
			? root.client_id.trim()
			: null;
	// Same tool in different conversations must not merge: scope the fold
	// to the MCP conversation/session when the row names one. Actor-less
	// session rows carry neither and keep tool-only keys.
	const metadata = (root.metadata ?? {}) as Record<string, unknown>;
	const scopeId =
		(typeof metadata.mcp_conversation_id === "string" &&
		metadata.mcp_conversation_id.trim()
			? metadata.mcp_conversation_id.trim()
			: null) ??
		(typeof metadata.mcp_session_id === "string" &&
		metadata.mcp_session_id.trim()
			? metadata.mcp_session_id.trim()
			: null);
	return { toolName, clientId, scopeId };
}

function toTime(value: unknown): number | null {
	if (value == null || value === "") return null;
	const time =
		value instanceof Date
			? value.getTime()
			: new Date(value as string).getTime();
	return Number.isNaN(time) ? null : time;
}

/**
 * Newest/oldest timestamps across nodes, independent of input order — the
 * feed also sorts ascending and by score, where position 0 is not newest.
 * Reply-only groups have no root; their first reply anchors them, otherwise
 * they sort as undated and lose their displayed date.
 */
function chronologicalBounds(nodes: TimelineNode[]): {
	newestAt: ContentItem["occurred_at"] | null;
	oldestAt: ContentItem["occurred_at"] | null;
} {
	let newest: { value: ContentItem["occurred_at"]; time: number } | null = null;
	let oldest: { value: ContentItem["occurred_at"]; time: number } | null = null;
	for (const node of nodes) {
		const value =
			node.group.root?.occurred_at ?? node.group.replies[0]?.occurred_at;
		const time = toTime(value);
		if (value == null || time == null) continue;
		if (!newest || time > newest.time) newest = { value, time };
		if (!oldest || time < oldest.time) oldest = { value, time };
	}
	return { newestAt: newest?.value ?? null, oldestAt: oldest?.value ?? null };
}

/**
 * Fold consecutive identical successful tool calls into one expandable node.
 * Client-side presentation over the loaded page only — like the supersede
 * and thread folds, it never reaches across unloaded rows, so counts can't
 * lie. The server keeps returning plain rows.
 */
export function foldConsecutiveToolCalls(
	nodes: TimelineNode[],
): FoldedTimelineEntry[] {
	const entries: FoldedTimelineEntry[] = [];
	let open:
		| {
				node: TimelineNode;
				toolName: string;
				clientId: string | null;
				scopeId: string | null;
		  }[]
		| null = null;
	let openKey: string | null = null;

	const flush = () => {
		if (open && open.length >= 2) {
			const [first] = open;
			const bounds = chronologicalBounds(open.map((item) => item.node));
			let totalDurationMs: number | null = null;
			let totalSdkCalls: number | null = null;
			for (const item of open) {
				const payload = (item.node.group.root?.payload_data ?? {}) as Record<
					string,
					unknown
				>;
				if (
					typeof payload.duration_ms === "number" &&
					Number.isFinite(payload.duration_ms) &&
					payload.duration_ms >= 0
				) {
					totalDurationMs = (totalDurationMs ?? 0) + payload.duration_ms;
				}
				if (
					typeof payload.sdk_call_count === "number" &&
					Number.isSafeInteger(payload.sdk_call_count) &&
					payload.sdk_call_count >= 0
				) {
					totalSdkCalls = (totalSdkCalls ?? 0) + payload.sdk_call_count;
				}
			}
			entries.push({
				kind: "fold",
				fold: {
					key: `fold:${first.node.rootId}:${open.length}`,
					toolName: first.toolName,
					clientId: first.clientId,
					nodes: open.map((item) => item.node),
					newestAt: bounds.newestAt,
					oldestAt: bounds.oldestAt,
					totalDurationMs,
					totalSdkCalls,
				},
			});
		} else if (open) {
			for (const item of open)
				entries.push({ kind: "single", node: item.node });
		}
		open = null;
		openKey = null;
	};

	for (const node of nodes) {
		const foldable = foldableAuditNode(node);
		const key = foldable
			? `${foldable.clientId ?? ""} ${foldable.scopeId ?? ""} ${foldable.toolName}`
			: null;
		if (key && key === openKey && open && foldable) {
			open.push({ node, ...foldable });
		} else {
			flush();
			if (key && foldable) {
				open = [{ node, ...foldable }];
				openKey = key;
			} else {
				entries.push({ kind: "single", node });
			}
		}
	}
	flush();
	return entries;
}

/**
 * One MCP conversation/session block: consecutive entries that happened in
 * the same conversation, whatever tools they ran. Failures belong to the
 * context too — nothing hides, it just groups.
 */
export interface ConversationBlock {
	/** First entry id + size: stable while the loaded set is unchanged. */
	key: string;
	clientId: string | null;
	activityId: string;
	activityKind: "conversation" | "session";
	entries: FoldedTimelineEntry[];
	newestAt: string | Date | number | null;
	oldestAt: string | Date | number | null;
}

export type FeedEntry =
	| FoldedTimelineEntry
	| { kind: "context"; block: ConversationBlock };

/** Conversation identity of one timeline node, or null when it has none
 *  worth grouping (runs, threads, and non-MCP rows keep their own nodes). */
function contextIdentity(node: TimelineNode): {
	clientId: string | null;
	activityId: string;
	activityKind: "conversation" | "session";
} | null {
	if (
		node.runId != null ||
		node.history.length > 0 ||
		node.group.replies.length > 0
	) {
		return null;
	}
	const root = node.group.root;
	if (!root) return null;
	if (
		root.semantic_type !== "audit" ||
		root.origin_type !== "tool_invocation"
	) {
		return null;
	}
	const metadata = (root.metadata ?? {}) as Record<string, unknown>;
	const conversationId =
		typeof metadata.mcp_conversation_id === "string" &&
		metadata.mcp_conversation_id.trim()
			? metadata.mcp_conversation_id.trim()
			: null;
	const sessionId =
		typeof metadata.mcp_session_id === "string" &&
		metadata.mcp_session_id.trim()
			? metadata.mcp_session_id.trim()
			: null;
	const activityId = conversationId ?? sessionId;
	if (!activityId) return null;
	const clientId =
		typeof root.client_id === "string" && root.client_id.trim()
			? root.client_id.trim()
			: null;
	return {
		clientId,
		activityId,
		activityKind: conversationId ? "conversation" : "session",
	};
}

function blockNodes(entries: FoldedTimelineEntry[]): TimelineNode[] {
	return entries.flatMap((entry) =>
		entry.kind === "fold" ? entry.fold.nodes : [entry.node],
	);
}

/**
 * Group consecutive same-conversation entries (singles and tool folds) into
 * titled blocks. Same honesty contract as every other fold: consecutive
 * loaded rows only, and a lone entry never becomes a one-row block.
 */
export function foldConversationContexts(
	entries: FoldedTimelineEntry[],
): FeedEntry[] {
	const out: FeedEntry[] = [];
	let open: FoldedTimelineEntry[] | null = null;
	let openKey: string | null = null;
	let openIdentity: {
		clientId: string | null;
		activityId: string;
		activityKind: "conversation" | "session";
	} | null = null;

	const flush = () => {
		if (open && open.length >= 2 && openIdentity) {
			const nodes = blockNodes(open);
			const first = nodes[0];
			const firstRootId = first?.group.root?.id ?? first?.rootId;
			const bounds = chronologicalBounds(nodes);
			out.push({
				kind: "context",
				block: {
					key: `ctx:${firstRootId}:${open.length}`,
					clientId: openIdentity.clientId,
					activityId: openIdentity.activityId,
					activityKind: openIdentity.activityKind,
					entries: open,
					newestAt: bounds.newestAt,
					oldestAt: bounds.oldestAt,
				},
			});
		} else if (open) {
			out.push(...open);
		}
		open = null;
		openKey = null;
		openIdentity = null;
	};

	for (const entry of entries) {
		const nodes = entry.kind === "fold" ? entry.fold.nodes : [entry.node];
		const identities = nodes.map(contextIdentity);
		const identity =
			identities.length > 0 &&
			identities.every(
				(identity) =>
					identity &&
					identity.clientId === identities[0]?.clientId &&
					identity.activityId === identities[0]?.activityId,
			)
				? (identities[0] ?? null)
				: null;
		const key = identity
			? `${identity.clientId ?? ""} ${identity.activityId}`
			: null;
		if (key && key === openKey && open) {
			open.push(entry);
		} else {
			flush();
			if (key && identity) {
				open = [entry];
				openKey = key;
				openIdentity = identity;
			} else {
				out.push(entry);
			}
		}
	}
	flush();
	return out;
}

/**
 * The run whose conversation an event belongs to. Approval/proposal events
 * carry a child internal run in `run_id`, with the producing Automation run
 * in `metadata.source_run_id` — and the transcript endpoint only serves
 * Automation runs, so opening the child yields an empty thread. Plain
 * produced events carry their Automation run directly.
 */
export function producingRunId(
	item: Pick<ContentItem, "run_id"> & { metadata?: unknown },
): number | null {
	const metadata =
		item.metadata && typeof item.metadata === "object"
			? (item.metadata as Record<string, unknown>)
			: null;
	const parent = metadata?.source_run_id;
	if (
		typeof parent === "number" &&
		Number.isSafeInteger(parent) &&
		parent > 0
	) {
		return parent;
	}
	return typeof item.run_id === "number" &&
		Number.isSafeInteger(item.run_id) &&
		item.run_id > 0
		? item.run_id
		: null;
}
/**
 * Drop stream nodes a pinned attention row already stands for. The only
 * structural identity the two share is the approval run: a pending approval's
 * notification carries the run id its event chain is keyed by, so while the
 * pinned row (with its own Approve/Reject) shows, the run's approval node
 * would be the same decision twice. Anything without that run key stays.
 */
export function withoutPinnedApprovals<
	N extends {
		runId: number | null;
		group: {
			root: { interaction_type?: string | null } | null;
			replies: { interaction_type?: string | null }[];
		};
	},
>(nodes: readonly N[], approvalRunIds: ReadonlySet<number>): N[] {
	if (approvalRunIds.size === 0) return [...nodes];
	return nodes.filter((node) => {
		const head = node.group.root ?? node.group.replies[0];
		return !(
			node.runId != null &&
			approvalRunIds.has(node.runId) &&
			head?.interaction_type === "approval"
		);
	});
}
